import React, { useState, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';

import { useGetProfileData } from 'hooks/useGetProfileData';

import { Guide } from 'components/features/Guide';
import { Profile } from 'components/features/Profile';
import { Story } from 'components/features/Story';
import { WalkThrough } from 'components/features/WalkThrough';
import { useCompleteOnboardingMutation, useGetProgressQuery } from 'store/progress';

import styles from './Tutorial.module.scss';

export enum TutorialPhase {
  STORY = 'STORY',
  PROFILE = 'PROFILE',
  WALKTHROUGH = 'WALKTHROUGH',
  TUTORIAL = 'TUTORIAL',
  DONE = 'DONE',
}

export const Tutorial: React.FC = () => {
  const { data } = useGetProfileData();
  const [trigger] = useCompleteOnboardingMutation();
  const { data: progressData, refetch } = useGetProgressQuery();
  const [phase, setPhase] = useState<TutorialPhase>(TutorialPhase.STORY);
  const navigate = useNavigate();

  const handleCompleteOnboarding = async () => {
    try {
      // オンボーディングの完了を送信する
      await trigger().unwrap();

      // リトライ回数上限
      const retryLimit = 5;
      let retryCount = 0;

      while (retryCount < retryLimit) {
        try {
          // ゲーム進捗状況（progress）をstoreに更新する
          const refetchedData = await refetch();

          // オンボーディングが完了していたらループを抜ける
          if (refetchedData.data?.isOnboardingCompleted) {
            setPhase(TutorialPhase.DONE);
            navigate('/home');
            return;
          }
        } catch (refetchError) {
          console.error('Failed to refetch progress:', refetchError);
        }

        // リトライ回数を増やす
        retryCount += 1;

        // オンボーディングが完了していなければ、再試行する前に一時停止
        // 次のリトライまでの待機（最後のリトライ後は待機しない）
        if (retryCount < retryLimit) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }

      // すべてのリトライが失敗した場合
      console.error('Failed to complete onboarding: exceeded retry attempts');
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
    }
  };

  const handleChangePhase = async () => {
    console.log(`Tutorial: ${phase}`);
    switch (phase) {
      case TutorialPhase.STORY:
        setPhase(TutorialPhase.PROFILE);
        navigate('/tutorial?phase=profile');
        break;
      case TutorialPhase.PROFILE:
        setPhase(TutorialPhase.WALKTHROUGH);
        navigate('/tutorial?phase=walkthrough');
        break;
      case TutorialPhase.WALKTHROUGH:
        setPhase(TutorialPhase.TUTORIAL);
        navigate('/tutorial?phase=tutorial');
        break;
      case TutorialPhase.TUTORIAL:
        await handleCompleteOnboarding();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (data && phase === TutorialPhase.PROFILE) {
      navigate('/tutorial?phase=walkthrough');
      setPhase(TutorialPhase.WALKTHROUGH);
    }
  }, [data, phase, navigate]);

  useEffect(() => {
    if (progressData?.isOnboardingCompleted && phase !== TutorialPhase.DONE) {
      setPhase(TutorialPhase.DONE);
    }
  }, [progressData, phase]);

  useEffect(() => {
    if (phase === TutorialPhase.DONE) {
      navigate('/home');
    }
  }, [phase, navigate]);

  return (
    <div className={[styles.wrap, phase === TutorialPhase.TUTORIAL && styles.fixWindow].join(' ')}>
      {phase === TutorialPhase.STORY && <Story onNextPhase={handleChangePhase} />}
      {phase === TutorialPhase.PROFILE && !data && (
        <Profile onSuccessSubmitCallback={handleChangePhase} />
      )}
      {phase === TutorialPhase.WALKTHROUGH && <WalkThrough handleClose={handleChangePhase} />}
      {phase === TutorialPhase.TUTORIAL && <Guide onNextPhase={handleChangePhase} />}
    </div>
  );
};
